import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { CommonModule } from '@angular/common';
import { ProviderSearchFilters, SearchFilter } from '../../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { Observable, of, Subscription } from 'rxjs';
import { SafeMethod } from '../../../../shared/decorators/safe-method.decorator';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { PostGetProviderSearchRequest } from '../../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-provider-search-filter-mobile',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCheckboxModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatChipsModule, MatAutocompleteModule],
  templateUrl: './provider-search-filter.component.html',
  styleUrl: './provider-search-filter.component.css'
})
export class ProviderSearchFilterComponent implements OnInit, OnDestroy {
  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);

  @Input() isPlus: boolean | null = null;
  @Input() currentLocation: LocationCoordinates | null = null;
  @Input() providerTypeAheadOptions: string[] = []; 
  @Input() cityZipTypeAheadOptions: string[] = []; 

  providerSearchQuery$ = this.searchState.providerSearchQuery$;
  providerSearchResults$ = this.searchState.providerSearchResults$;
  providerSearchFilters$ = this.searchState.providerSearchFilters$;
  providerSortMethod$ = this.searchState.providerSortMethod$;
  
  currentFilters: ProviderSearchFilters | null = null;
  isLoading: boolean = true;
  hasError: boolean = false;
  providerSearchResults: any[] = [];
  providerSortMethod: 'distance' | 'availability' | 'name' = 'distance';

  // Provider name autocomplete
  filteredProviderOptions$!: Observable<string[]>;

  // Specialty autocomplete
  specialtySearchText: string = '';
  filteredSpecialties$!: Observable<SearchFilter[]>;
  allSpecialties: SearchFilter[] = [];
  selectedSpecialties: SearchFilter[] = [];
  specialtyCount: number = 0;

  // Language autocomplete
  languageSearchText: string = '';
  filteredLanguages$!: Observable<SearchFilter[]>;
  allLanguages: SearchFilter[] = [];
  selectedLanguages: SearchFilter[] = [];
  additionalSelectedLanguages: SearchFilter[] = [];
  languageCount: number = 0;

  // Search query context
  providerNameText: string = '';
  queryNameOrSpecialty: string = '';
  queryCityOrZip: string = '';

  // Show all toggles
  showAllSpecialties: boolean = false;
  showAllLanguages: boolean = false;

  // Track which sections are expanded
  expandedSections: { [key: string]: boolean } = {
    specialty: false,
    gender: false,
    affiliation: false,
    language: false,
    onlineScheduling: false
  };

  locale = "en-US";
  searchPlaceholderText = "Search";
  providerLabel = "Provider Name";
  specialtyLabel = "Specialty";
  onlineSchedLabel = "Online Scheduling";
  medGroupLabel = "Medical Group";
  genderLabel = "Gender";
  langLabel = "Languages";
  acceptedInsuranceLabel = "Accepted Insurance";
  clearBtnText = "Clear all";
  applyBtnText = "Apply";


  private subscription?: Subscription;

  constructor(private translate: TranslateService) {
    
    this.locale =  sessionStorage.getItem('locale') || 'en-US';
    this.translate.use(this.locale); // Default to 'en-US' if no locale is provided
    
  }

  ngOnInit(): void {
    // Initialize provider name autocomplete
    this.filteredProviderOptions$ = of(this.providerTypeAheadOptions);

    //translations
    this.translate.get('SEARCH').subscribe((translated: string) => {
      this.searchPlaceholderText = translated;
    });

    this.translate.get('FILTERS.PROVIDER_NAME_SPECIALTY').subscribe((translated: string) => {
      this.providerLabel = translated;
    });

    this.translate.get('FILTERS.SPECIALTIES').subscribe((translated: string) => {
      this.specialtyLabel = translated;
    });

    this.translate.get('FILTERS.ACCEPTED_INSURANCE').subscribe((translated: string) => {
      this.acceptedInsuranceLabel = translated;
    });
    this.translate.get('FILTERS.ONLINE_SCHEDULING').subscribe((translated: string) => {
      this.onlineSchedLabel = translated;
    });
    this.translate.get('FILTERS.GENDER').subscribe((translated: string) => {
      this.genderLabel = translated;
    });
    this.translate.get('FILTERS.LANGUAGES').subscribe((translated: string) => {
      this.langLabel = translated;
    });
    this.translate.get('FILTERS.MEDICAL_GROUP').subscribe((translated: string) => {
      this.medGroupLabel = translated;
    });

    this.translate.get('APPLY').subscribe((translated: string) => {
      this.applyBtnText = translated;
    });

    this.translate.get('CLEAR_ALL').subscribe((translated: string) => {
      this.clearBtnText = translated;
    });

    this.filteredProviderOptions$ = of([]);

    // state listeners
    try {
      this.providerSearchQuery$.subscribe(query => {
        if (query) {
          this.queryNameOrSpecialty = query.nameOrSpecialty;
          this.providerNameText = query.nameOrSpecialty;
          this.queryCityOrZip = query.cityOrZip;
        }
      });

      this.providerSearchResults$.subscribe(results => {
        this.providerSearchResults = results ?? [];
      });

      this.providerSortMethod$.subscribe(sortBy => {
        this.providerSortMethod = sortBy;
      });

      this.subscription = this.providerSearchFilters$.subscribe({
        next: (filters: ProviderSearchFilters | null) => {
          this.currentFilters = filters;

          // Initialize specialty list
          if (filters && filters.specialtyList) {
            this.allSpecialties = filters.specialtyList;
            this.selectedSpecialties = filters.specialtyList.filter(option => option.isSelected == true);
            this.specialtyCount = this.selectedSpecialties.length;
            // Initialize with unselected specialties only
            this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));
          }

          // Initialize language list
          if (filters && filters.languageList) {
            this.allLanguages = filters.languageList;
            this.selectedLanguages = filters.languageList.filter(option => option.isSelected == true);
            this.languageCount = this.selectedLanguages.length;
            
            // Repopulate additionalSelectedLanguages with selected languages not in first 5
            this.additionalSelectedLanguages = [];
            this.selectedLanguages.forEach(lang => {
              const languageIndex = this.allLanguages.indexOf(lang);
              if (languageIndex >= 5) {
                this.additionalSelectedLanguages.push(lang);
              }
            });
            
            // Initialize with languages not in the displayed list (first 5 + additional)
            const displayedLanguages = this.getDisplayedLanguages();
            this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
          }

          this.isLoading = false;
          this.hasError = false;
        },
        error: (err) => {
          console.error('Error loading filters:', err);
          this.hasError = true;
          this.isLoading = false;
        }
      });
    } catch (error) {
      console.error('Error initializing filter component:', error);
      this.hasError = true;
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  @SafeMethod()
  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  // Provider Name Autocomplete Methods
  filterProviderNames(searchText: string): void {
    if (!searchText || searchText.trim().length < 3) {
      this.filteredProviderOptions$ = of([]);
    } else {
      const filterValue = searchText.toLowerCase();
      const filtered = this.providerTypeAheadOptions.filter(option =>
        this._fuzzyMatch(filterValue, option) || option.toLowerCase().includes(filterValue.toLowerCase())
      );
      this.filteredProviderOptions$ = of(filtered);
    }
  }

  // Specialty Autocomplete Methods
  filterSpecialties(searchText: string): void {
    if (!searchText || searchText.trim() === '') {
      // Filter out already selected specialties
      this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));
    } else {
      const filterValue = searchText.toLowerCase();
      // Filter by search text AND exclude already selected specialties
      const filtered = this.allSpecialties.filter(specialty =>
        specialty.value.toLowerCase().includes(filterValue) && !specialty.isSelected
      );
      this.filteredSpecialties$ = of(filtered);
    }
  }

  onSpecialtySelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    const specialty = this.allSpecialties.find(s => s.value === selectedValue);

    if (specialty && !specialty.isSelected) {
      specialty.isSelected = true;
      this.selectedSpecialties = this.allSpecialties.filter(s => s.isSelected);
      this.specialtyCount = this.selectedSpecialties.length;
      console.log('Specialty added:', specialty);
    }

    // Clear the search text after selection
    setTimeout(() => {
      this.specialtySearchText = '';
      // Filter out selected specialties from the list
      this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));
    }, 0);
  }

  removeSpecialty(specialty: SearchFilter): void {
    specialty.isSelected = false;
    this.selectedSpecialties = this.allSpecialties.filter(s => s.isSelected);
    this.specialtyCount = this.selectedSpecialties.length;

    // Refresh the filtered list to include the removed specialty
    this.filteredSpecialties$ = of(this.allSpecialties.filter(s => !s.isSelected));

    console.log('Specialty removed:', specialty);
  }

  // Language Autocomplete Methods
  filterLanguages(searchText: string): void {
    // Get all languages that are currently displayed as checkboxes
    const displayedLanguages = this.getDisplayedLanguages();

    if (!searchText || searchText.trim() === '') {
      // Filter out all displayed languages
      this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
    } else {
      const filterValue = searchText.toLowerCase();
      // Filter by search text AND exclude all displayed languages
      const filtered = this.allLanguages.filter(language =>
        language.value.toLowerCase().includes(filterValue) && !displayedLanguages.includes(language)
      );
      this.filteredLanguages$ = of(filtered);
    }
  }

  onLanguageSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    const language = this.allLanguages.find(l => l.value === selectedValue);

    if (language && !language.isSelected) {
      language.isSelected = true;
      this.selectedLanguages = this.allLanguages.filter(l => l.isSelected);
      this.languageCount = this.selectedLanguages.length;

      // Track if this language is not in the first 5
      const languageIndex = this.allLanguages.indexOf(language);
      if (languageIndex >= 5 && !this.additionalSelectedLanguages.includes(language)) {
        this.additionalSelectedLanguages.push(language);
      }

      console.log('Language added:', language);
    }

    // Clear the search text after selection
    setTimeout(() => {
      this.languageSearchText = '';
      // Filter out all displayed languages from the list
      const displayedLanguages = this.getDisplayedLanguages();
      this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));
    }, 0);
  }

  removeLanguage(language: SearchFilter): void {
    language.isSelected = false;
    this.selectedLanguages = this.allLanguages.filter(l => l.isSelected);
    this.languageCount = this.selectedLanguages.length;

    // Remove from additional languages if present
    const index = this.additionalSelectedLanguages.indexOf(language);
    if (index > -1) {
      this.additionalSelectedLanguages.splice(index, 1);
    }

    // Refresh the filtered list to exclude all displayed languages
    const displayedLanguages = this.getDisplayedLanguages();
    this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));

    console.log('Language removed:', language);
  }

  @SafeMethod()
  onLanguageCheckboxChange(language: SearchFilter): void {
    language.isSelected = !language.isSelected;
    this.selectedLanguages = this.allLanguages.filter(l => l.isSelected);
    this.languageCount = this.selectedLanguages.length;

    const languageIndex = this.allLanguages.indexOf(language);
    
    if (language.isSelected && languageIndex >= 5) {
      // Add to additional languages if not already there
      if (!this.additionalSelectedLanguages.includes(language)) {
        this.additionalSelectedLanguages.push(language);
      }
    } else if (!language.isSelected && languageIndex >= 5) {
      // Remove from additional languages if deselected
      const index = this.additionalSelectedLanguages.indexOf(language);
      if (index > -1) {
        this.additionalSelectedLanguages.splice(index, 1);
      }
    }

    console.log('Language checkbox changed:', language, 'isSelected:', language.isSelected);
  }

  getDisplayedLanguages(): SearchFilter[] {
    const firstFive = this.allLanguages.slice(0, 5);
    const result = [...firstFive];

    // Add additional selected languages that are not in the first 5
    this.additionalSelectedLanguages.forEach(lang => {
      if (!result.includes(lang)) {
        result.push(lang);
      }
    });

    return result;
  }

  @SafeMethod()
  onGenderChange(option: SearchFilter): void {
    if (this.currentFilters?.genderList) {
      // Deselect all other gender options
      this.currentFilters.genderList.forEach(g => {
        if (g.value != option.value) {
          g.isSelected = false;
        }
      });
    }
  }

  @SafeMethod()
  onFilterChange(filterList: SearchFilter[], option: SearchFilter): void {
    option.isSelected = !option.isSelected;
  }

  @SafeMethod()
  applyFilters(): void {
    console.log('Applying Filters:', this.currentFilters);
    this.searchState.setShowSpinner(true);

    // Build search request with all filters
    var request = new PostGetProviderSearchRequest();

    request.nameOrSpecialty = this.providerNameText;
    request.cityOrZip = this.queryCityOrZip;
    request.distanceInMiles = this.currentFilters?.distanceInMiles ?? 15;

    request.specialties = this.currentFilters?.specialtyList?.filter(s => s.isSelected).map(s => s.value).join(', ');
    request.onlineScheduling = this.currentFilters?.onlineSchedulingList?.some(o => o.isSelected) ? true : undefined;
    request.affiliations = this.currentFilters?.affiliationList?.filter(a => a.isSelected).map(a => a.value).join(', ');
    request.gender = this.currentFilters?.genderList?.find(g => g.isSelected)?.value ?? '';
    request.languages = this.currentFilters?.languageList?.filter(l => l.isSelected).map(l => l.value).join(', ');

    // Only set the latitude and longitude if a cityOrZip value is not provided
    if ((request.cityOrZip == null || request.cityOrZip == "") && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude) {
      request.latitude = this.currentLocation.latitude;
      request.longitude = this.currentLocation.longitude;
    }

    this.mcAppGateway.PostGetProviderSearch(request).subscribe({
      next: (response) => {
        var sortedResponse = this.searchState.sortProviderSearchResults(response || [], this.providerSortMethod);
        console.log('Provider Search Response (after applying filters):', sortedResponse);
        this.searchState.setProviderSearchResults(sortedResponse);
        this.searchState.setProviderSearchFilters(this.currentFilters!);
        this.searchState.setProviderSearchQuery({
          nameOrSpecialty: this.providerNameText,
          cityOrZip: this.queryCityOrZip,
        });

        this.searchState.setShowSpinner(false);
        this.searchState.setProviderSearchFilterToggle(false);
      },
      error: (error) => {
        console.error('An error occurred while searching for providers with filters: ', error);
        alert('An error occurred while searching for providers with filters: ' + error.message);

        this.searchState.setShowSpinner(false);
      },
    });
  }

  @SafeMethod()
  clearFilters(): void {
    if (this.currentFilters) {
      // Clear all selections
      // this.currentFilters.specialtyList?.forEach(option => option.isSelected = false);
      // this.currentFilters.genderList?.forEach(option => option.isSelected = false);
      // this.currentFilters.affiliationList?.forEach(option => option.isSelected = false);
      // this.currentFilters.languageList?.forEach(option => option.isSelected = false);
      // this.currentFilters.onlineSchedulingList?.forEach(option => option.isSelected = false);

      // Reset specialty and language selections
      this.selectedSpecialties = [];
      this.selectedLanguages = [];
      this.additionalSelectedLanguages = [];
      this.specialtyCount = 0;
      this.languageCount = 0;

      // Refresh filtered lists
      this.filteredSpecialties$ = of(this.allSpecialties);
      const displayedLanguages = this.getDisplayedLanguages();
      this.filteredLanguages$ = of(this.allLanguages.filter(l => !displayedLanguages.includes(l)));

      // this.searchState.setProviderSearchFilters(this.currentFilters);
    }
  }

  @SafeMethod()
  closeFilter(): void {
    if(this.currentFilters) {
      this.searchState.setProviderSearchFilters(this.currentFilters) ;
    }
    
    this.searchState.setProviderSearchFilterToggle(false);
  }

  @SafeMethod()
  getSelectedCount(filterList: SearchFilter[]): number {
    return filterList?.filter(option => option.isSelected).length || 0;
  }

  @SafeMethod([])
  getFilteredOptions(filterList: SearchFilter[], searchTerm: string): SearchFilter[] {
    if (!filterList) return [];
    if (!searchTerm || searchTerm.trim() === '') return filterList;

    const term = searchTerm.toLowerCase();
    return filterList.filter(option =>
      option.value?.toLowerCase().includes(term)
    );
  }

  private _fuzzyMatch(filterValue: string, option: string): boolean {
    // Remove spaces, special charaters, and convert to lowercase for comparison
    const normalizedFilter = filterValue.toLowerCase().replace(/\s/g, '');
    const normalizedOption = option.toLowerCase().replace(/\s/g, '').replace(/[^a-z0-9]/g, '');
    
    return normalizedOption.includes(normalizedFilter)
  }
}
